import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, toast, type SegmentedTabItem } from '@/ui';
import { marketplaceKeys } from '@/lib/task-query-keys';
import { filtersToServerParams } from '@/lib/task-list-url';
import { useServerList } from '@/lib/task-server-list';
import { useSessionStore } from '@/auth/session.store';
import type { OfferRow } from '@/features/tasks/types';
import { useUrlList } from '@/features/tasks/useUrlList';
import { useUpdateTask } from '@/features/tasks/queries';
import { hasPermission } from '@/auth/types';
import { ListScreen } from '@/features/tasks/components/ListScreen';
import { QuoteActions } from '@/features/tasks/components/QuoteCard';
import { taskErrorMessage } from '@/features/tasks/error-message';
import { fetchOfferPage } from './api';
import { OfferCard } from './OfferCard';
import { PayoutDialog } from './PayoutDialog';
import {
  DEFAULT_OFFER_TAB as DEFAULT_TAB,
  OFFER_TABS as TABS,
  exchangeOfferTabParams,
  hasOrganizationOfferScope,
  type OfferTab as Tab,
} from './tab-params';

/**
 * TEKLIFLERIM — buronun verdigi teklifler.
 *
 * ======================= IKI ADIMLI ONAY (2026-09-26) =======================
 * KAZANILAN iste buronun IKI aksiyonu var (ilk surum 2026-09-07'de tek adimdi ve "Odeme
 * alindi" isi dogrudan 'completed'a cekiyordu; buronun gonderimi hicbir durumda gorunmuyordu):
 *   Bekleyenler : 'pending' satirda "Odeme alindi"   -> 'payment_received' (is Odenecekler'e gecer)
 *   Odenecekler : 'payment_received' satirda "Islemi tamamla" -> gonderim penceresi -> 'completed'
 * Onaylanmayan adimda pencere + grace dolunca supurucu isi 'warning'e ceker ve iki tarafa
 * bildirir. Uyari alan is platform yonetiminde 'resolved' / 'refunded' ile kapanir.
 *
 * Teklif duzenleme yine Piyasa'dan yurur — orada gorevin fazi da gorunuyor ve teklif ancak
 * 'offer' fazinda degistirilebilir.
 *
 * Sekme -> sunucu parametresi eslemesi ./tab-params.ts'te (Bekleyenler `offerTab=waiting`).
 *
 * ======================= KAZANAN BURO, KISI DEGIL (2026-09-23) ===================
 * Sunucu iki adimi da kazanan teklifin ORGANIZASYONUNUN aktif ve `completeTask` yetkili
 * her uyesine aciyor (yonetici ortuk yetkili; org'suz buroda yalniz teklifi veren). Teklifi
 * veren personel izinli ya da ekipten cikmis olsa bile is kapanabilsin diye. Bu yuzden
 * Bekleyenler ve Odenecekler yetkili org uyesinde `offerScope=organization` ister ve sunucu
 * org'un kazanilan islerini (cagiranin kendileri dahil) doner.
 *
 * 2026-10-03 (K2, bir buro bir teklif): org kapsami `createOffer` / `editOffer` / `completeTask`
 * yetkilerinden birine sahip her org uyesinde (tab-params.ts hasOrganizationOfferScope). Aksiyon
 * dugmeleri yine yalniz `completeTask`'ta.
 */

const isTab = (value: string): value is Tab => (TABS as readonly string[]).includes(value);

const SORT_OPTIONS = [
  { value: 'offerCreatedAt:desc', labelKey: 'filter.sortOfferNewest' },
  { value: 'offerPrice:asc', labelKey: 'filter.sortPriceAsc' },
  { value: 'offerPrice:desc', labelKey: 'filter.sortPriceDesc' },
] as const;

export function ExchangeOffersPage() {
  const { t } = useTranslation();
  const userId = useSessionStore((state) => state.snapshot?.user.id);
  const url = useUrlList(DEFAULT_TAB);
  const tab: Tab = isTab(url.tab) ? url.tab : DEFAULT_TAB;

  // Iki adimin da kapsami KAZANAN BURO ve yetkisi `completeTask` (task-status.js, app-service
  // postUpdateTask); buradaki kontroller yalnizca UI, asil kapi sunucuda.
  const snapshot = useSessionStore((state) => state.snapshot);
  const canComplete = hasPermission(snapshot, 'completeTask');
  // 1. adim: "Odeme alindi" onayi.
  const [confirming, setConfirming] = useState<OfferRow | null>(null);
  // 2. adim: gonderim penceresi ("Islemi tamamla").
  const [paying, setPaying] = useState<OfferRow | null>(null);
  const updateTask = useUpdateTask();

  // ORG KAPSAMI teklif veren / guncelleyen / tamamlayan ORG UYESINDE (sunucu kurali; org'suz
  // buroda kapsam hic acilmiyor ve kendi teklifleri doner). Kural ve sekmeler tab-params.ts'te.
  const organizationScope = hasOrganizationOfferScope(snapshot);

  const params = useMemo(
    () =>
      userId
        ? {
            ...exchangeOfferTabParams(tab, { userId, organizationScope }),
            ...filtersToServerParams(url.filters),
          }
        : {},
    [userId, tab, url.filters, organizationScope],
  );

  const list = useServerList<OfferRow>({
    baseKey: marketplaceKeys.offerLists(),
    params,
    fetchPage: fetchOfferPage,
    offset: url.offset,
    onParamsChanged: url.resetOffset,
    enabled: Boolean(userId),
    // Musterinin adimlari (onay) web'e push ile gelmiyor; etkin sekmeler yoklanir.
    pollMs: tab === 'passedOffers' ? undefined : 15_000,
  });

  const tabs: SegmentedTabItem[] = [
    { value: 'openOffers', label: t('task.tabs.pending') },
    { value: 'paymentWaitingOffers', label: t('task.tabs.toPay') },
    { value: 'passedOffers', label: t('task.tabs.past') },
  ];

  const renderRow = useCallback(
    (offer: OfferRow) => {
      // Aksiyon SATIRIN durumundan okunur (sekmeden degil): her durum zaten tek sekmede.
      // 'warning' icin aksiyon YOK (2026-09-23): uyari alan is platform yonetiminin kuyruguna
      // duser ve orada 'resolved' / 'refunded' ile kapanir.
      const won = canComplete && offer.isAccepted;
      const canConfirmPayment = won && offer.taskStatusCode === 'pending';
      const canCompleteTransaction = won && offer.taskStatusCode === 'payment_received';

      return (
        <OfferCard
          offer={offer}
          receivedAtMs={list.receivedAtMs}
          side="exchange"
          // Karar verilmisse rozet: "Kazandiniz" / "Baska buroya verildi" (M1).
          taskDecided={offer.pretenderUserId != null}
          footer={
            canConfirmPayment || canCompleteTransaction ? (
              <QuoteActions>
                {canConfirmPayment ? (
                  <Button
                    type="button"
                    size="sm"
                    tone="primary"
                    onClick={() => setConfirming(offer)}
                  >
                    {t('task.tabs.paymentReceived')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    tone="primary"
                    onClick={() => setPaying(offer)}
                  >
                    {t('task.tabs.completeTransaction')}
                  </Button>
                )}
              </QuoteActions>
            ) : undefined
          }
        />
      );
    },
    [list.receivedAtMs, canComplete, t],
  );

  return (
    <>
      <ListScreen
        title={t('task.myOffersTitle')}
        tabs={tabs}
        tab={tab}
        onTabChange={url.setTab}
        filters={url.filters}
        onFiltersChange={url.setFilters}
        sortOptions={SORT_OPTIONS}
        list={list}
        onOffsetChange={url.setOffset}
        onClearFilters={() => {
          url.setFilters({
            ...url.filters,
            q: '',
            currencies: [],
            createdFrom: '',
            createdTo: '',
            amountMin: '',
            amountMax: '',
          });
        }}
        rowKey={(offer) => offer.id}
        renderRow={renderRow}
      />
      <Dialog
        open={confirming !== null}
        title={t('task.tabs.paymentReceivedTitle')}
        description={t('task.tabs.paymentReceivedBody')}
        confirmLabel={t('task.tabs.paymentReceived')}
        cancelLabel={t('actions.cancel')}
        busy={updateTask.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (!confirming) return;
          // Isi BITIRMEZ: 'payment_received' gonderim adimini acar (Odenecekler).
          updateTask.mutate(
            { taskId: confirming.taskId, taskStatus: 'payment_received' },
            {
              onSuccess: () => {
                setConfirming(null);
                toast({ message: t('task.tabs.paymentReceivedDone'), tone: 'success' });
              },
              onError: (error) => {
                toast({ message: taskErrorMessage(error, t), tone: 'danger' });
              },
            },
          );
        }}
      />
      {paying === null ? null : (
        <PayoutDialog
          offer={paying}
          busy={updateTask.isPending}
          onCancel={() => setPaying(null)}
          onConfirm={() => {
            updateTask.mutate(
              { taskId: paying.taskId, taskStatus: 'completed' },
              {
                onSuccess: () => {
                  setPaying(null);
                  toast({ message: t('task.tabs.completeTransactionDone'), tone: 'success' });
                },
                onError: (error) => {
                  toast({ message: taskErrorMessage(error, t), tone: 'danger' });
                },
              },
            );
          }}
        />
      )}
    </>
  );
}
